/**
 * El alta de Stripe, vista desde cualquier pantalla que no sea Configuración.
 *
 * Nació dentro del asistente de servicios reservables y salió de ahí en cuanto
 * la lista de servicios necesitó lo mismo: saber si la cuenta puede cobrar,
 * abrir el alta sin perder lo que hay en pantalla y enterarse de que ha
 * terminado. Copiarlo habría dejado dos sitios donde arreglar el día que Stripe
 * cambie algo, y el segundo se habría quedado atrás.
 *
 * Lo que NO hace es pintar: la ventana de espera —`StripeOnboardingDialog`— la
 * pone quien llama, porque cada pantalla decide dónde va dentro de su árbol.
 */
import { useCallback, useEffect, useState } from 'react'

import { getStripeConnection, startStripeOnboarding, type ConnectStatus } from './api'
import { translate } from './i18n'

export type StripeConnect = {
  /** Si la cuenta puede cobrar. `null` mientras no se sabe. */
  ready: boolean | null
  /** El estado completo, que es lo que lee la ventana de espera. */
  status: ConnectStatus
  /** Abierta mientras el alta ocurre en la otra pestaña. */
  awaiting: boolean
  /** Pidiendo el enlace. Mientras tanto el botón no repite. */
  connecting: boolean
  /** Lo que salió mal al pedirlo, ya en castellano. Vacío si no hay nada. */
  error: string
  connect: () => Promise<void>
  recheck: () => Promise<void>
  /** Cierra la espera a mano y vuelve a preguntar por si acaso ya está. */
  dismiss: () => void
}

export function useStripeConnect(projectId: string, token: string, describeError: (error: unknown) => string): StripeConnect {
  const [ready, setReady] = useState<boolean | null>(null)
  /* El estado COMPLETO, no sólo si puede cobrar: la ventana de espera distingue
     «sin cuenta» de «alta a medias» de «Stripe revisando», y con un booleano
     diría lo mismo en los tres casos. */
  const [status, setStatus] = useState<ConnectStatus>('none')
  const [awaiting, setAwaiting] = useState(false)
  const [connecting, setConnecting] = useState(false)
  const [error, setError] = useState('')

  const recheck = useCallback(async () => {
    try {
      const connection = await getStripeConnection(projectId, token)
      setStatus(connection.status)
      setReady(connection.status === 'ready')
    } catch {
      /* Si no se sabe, no se bloquea: el servidor sigue siendo quien decide, y
         un fallo de red nuestro no puede impedirle publicar a quien sí puede. */
      setReady(null)
    }
  }, [projectId, token])

  useEffect(() => { void recheck() }, [recheck])

  /*
   * Al volver a esta pestaña se vuelve a preguntar.
   *
   * El alta de Stripe ocurre FUERA, en otra pestaña. Sin esto, quien la termina
   * y vuelve se encuentra el aviso igual que lo dejó y la opción todavía
   * bloqueada, como si no hubiera servido de nada.
   */
  useEffect(() => {
    const onFocus = () => { if (ready !== true) void recheck() }
    window.addEventListener('focus', onFocus)
    return () => { window.removeEventListener('focus', onFocus) }
  }, [recheck, ready])

  /*
   * Con la espera abierta se pregunta sola, cada cinco segundos.
   *
   * El foco no basta: mucha gente deja las dos pestañas a la vista en pantallas
   * anchas y nunca vuelve a hacer clic aquí. Sin esto, la ventana se quedaría
   * girando indefinidamente al lado de una cuenta que ya está lista.
   */
  useEffect(() => {
    if (!awaiting || status === 'ready') return
    const timer = window.setInterval(() => { void recheck() }, 5000)
    return () => { window.clearInterval(timer) }
  }, [awaiting, status, recheck])

  /**
   * Abre el alta de Stripe sin salir de donde se está.
   *
   * En otra pestaña, y ésa es la decisión importante: llevarse ésta a Stripe
   * tiraría lo que haya a medio rellenar por hacer un trámite que se puede
   * hacer al lado.
   *
   * La pestaña se abre ANTES de pedir el enlace: después del `await` el
   * navegador ya no lo cuenta como un gesto de la persona y lo bloquea como
   * ventana emergente. Es la misma cuenta que hace `StripeSettings`.
   */
  const connect = useCallback(async () => {
    if (connecting) return
    const tab = window.open('about:blank', '_blank')
    setConnecting(true)
    setError('')
    try {
      const { url } = await startStripeOnboarding(projectId, token)
      if (tab) {
        tab.location.replace(url)
        /* La espera se abre AQUÍ y no antes: hasta que hay enlace no hay nada
           que esperar, y abrirla antes la dejaría puesta si la petición falla. */
        setAwaiting(true)
      }
      /* Sin pestaña —un bloqueador muy estricto— no se lleva ésta a Stripe: se
         dice y se deja decidir, que perder el alta a medias sería peor que no
         poder conectar ahora. */
      else setError(translate('stripe.popupBlocked'))
    } catch (connectError) {
      tab?.close()
      setError(describeError(connectError))
    } finally {
      setConnecting(false)
    }
  }, [connecting, projectId, token, describeError])

  const dismiss = useCallback(() => { setAwaiting(false); void recheck() }, [recheck])

  return { ready, status, awaiting, connecting, error, connect, recheck, dismiss }
}
